/**
 * Post-purchase 1-Click Upsell — the browser half (feature 0042 lot E).
 *
 * The two calls the confirmation page makes, and nothing else. The offer CARD, the money rules and every type
 * are shared with the pre-purchase chain ({@link ./upsell.ts}) — the buyer sees the same offer whether it
 * plays before the form or after the order, because it IS the same offer.
 *
 * The one thing that differs, and it is the whole reason this file exists: the order already EXISTS. So the
 * accept is a server write whose answer is authoritative — the page re-renders the order the server returns,
 * and never adds a line to a total of its own.
 */
/** The read is decoration on a page that already succeeded — it never waits long. */
export const POST_PURCHASE_FETCH_TIMEOUT_MS = 4_000;
/** The write moves money, so it is given room to finish; a buyer is watching a spinner meanwhile. */
export const POST_PURCHASE_ACCEPT_TIMEOUT_MS = 10_000;
/**
 * Fetch the chain to play on this order's confirmation page.
 *
 * Returns null for EVERY negative outcome — nothing to play, an error, a timeout, an unparseable body. The
 * page has already told the buyer their order is confirmed; nothing here is worth disturbing that.
 */
export async function fetchPostPurchaseChain(orderId, opts = {}) {
    const doFetch = opts.fetchImpl ?? (typeof fetch !== 'undefined' ? fetch : undefined);
    if (!doFetch || !orderId)
        return null;
    const params = new URLSearchParams({ orderId });
    if (opts.store)
        params.set('store', opts.store);
    try {
        const res = await withTimeout((signal) => doFetch(`/api/order-upsell?${params.toString()}`, {
            method: 'GET',
            headers: { Accept: 'application/json' },
            ...(signal ? { signal } : {}),
        }), opts.timeoutMs ?? POST_PURCHASE_FETCH_TIMEOUT_MS);
        if (res.status !== 200)
            return null;
        const body = (await res.json());
        if (!body || !Array.isArray(body.offers) || body.offers.length === 0)
            return null;
        return body;
    }
    catch {
        return null;
    }
}
/**
 * Accept ONE offer onto the order.
 *
 * Sends `(upsellId, version, offerId, variantId, quantity)` — no money, ever. Returns null when the call
 * could not complete at all (network, timeout, 4xx/5xx): the caller then keeps showing the order it already
 * has, which is the truth, because nothing was written.
 */
export async function acceptPostPurchaseOffer(orderId, acceptance, opts = {}) {
    const doFetch = opts.fetchImpl ?? (typeof fetch !== 'undefined' ? fetch : undefined);
    if (!doFetch || !orderId)
        return null;
    const params = new URLSearchParams({ orderId });
    if (opts.store)
        params.set('store', opts.store);
    try {
        const res = await withTimeout((signal) => doFetch(`/api/order-upsell?${params.toString()}`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify(acceptance),
            ...(signal ? { signal } : {}),
        }), opts.timeoutMs ?? POST_PURCHASE_ACCEPT_TIMEOUT_MS);
        // 200 = settled (the line landed, or it did not and the order says so). 202 = the bank wants the buyer
        // to authenticate: also a real answer, and the one case where no order comes back, because nothing was
        // written. Anything else is treated as "no answer" and the confirmed order the page shows stands.
        if (res.status !== 200 && res.status !== 202)
            return null;
        const body = (await res.json());
        if (!body || typeof body.accepted !== 'boolean')
            return null;
        if (res.status === 202)
            return body.clientSecret && body.stripeAccountId ? body : null;
        return body.order ? body : null;
    }
    catch {
        return null;
    }
}
/**
 * Finish an acceptance the bank interrupted (feature 0069 lot E).
 *
 * The buyer has just answered the 3-D Secure challenge in their browser. This asks the SERVER whether the
 * money actually landed — never the browser's word for it — and the server writes the line only if it did.
 *
 * Same shape and same silence as {@link acceptPostPurchaseOffer}: null for every outcome that is not a
 * settled answer, because the page has already told the buyer their order is confirmed and nothing here is
 * worth disturbing that.
 */
export async function settlePostPurchaseOffer(orderId, acceptance, opts = {}) {
    const doFetch = opts.fetchImpl ?? (typeof fetch !== 'undefined' ? fetch : undefined);
    if (!doFetch || !orderId)
        return null;
    const params = new URLSearchParams({ orderId, settle: '1' });
    if (opts.store)
        params.set('store', opts.store);
    try {
        const res = await withTimeout((signal) => doFetch(`/api/order-upsell?${params.toString()}`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify(acceptance),
            ...(signal ? { signal } : {}),
        }), opts.timeoutMs ?? POST_PURCHASE_ACCEPT_TIMEOUT_MS);
        if (res.status !== 200)
            return null;
        const body = (await res.json());
        if (!body || typeof body.accepted !== 'boolean' || !body.order)
            return null;
        return body;
    }
    catch {
        return null;
    }
}
/** Run a fetch under an abort timer, in environments that have one; degrade gracefully where they don't. */
async function withTimeout(run, timeoutMs) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
        return await run(controller?.signal);
    }
    finally {
        if (timer)
            clearTimeout(timer);
    }
}
//# sourceMappingURL=postPurchaseUpsell.js.map